import { Button } from "@/components/ui/button";
import { CommunicationService } from "@/gen/omnios/v1/communication_connect";
import { ConversationKind } from "@/gen/omnios/v1/communication_pb";
import { transport } from "@/lib/grpc";
import { cn } from "@/lib/utils";
import { createClient } from "@connectrpc/connect";
import { useNavigate } from "@tanstack/react-router";
import {
	ArrowRight,
	Globe,
	Loader2,
	MessageCircle,
	Package,
	ShoppingCart,
	Sparkles,
	Zap,
} from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useOnboarding } from "./OnboardingContext";

const VALUE_PROPS = [
	{
		icon: Package,
		titleKey: "vp_catalog_title",
		descKey: "vp_catalog_desc",
		color: "bg-blue-500/10 text-blue-600",
	},
	{
		icon: Sparkles,
		titleKey: "vp_orders_title",
		descKey: "vp_orders_desc",
		color: "bg-amber-500/10 text-amber-600",
	},
	{
		icon: ShoppingCart,
		titleKey: "vp_management_title",
		descKey: "vp_management_desc",
		color: "bg-emerald-500/10 text-emerald-600",
	},
	{
		icon: Zap,
		titleKey: "vp_automation_title",
		descKey: "vp_automation_desc",
		color: "bg-violet-500/10 text-violet-600",
	},
];

const commClient = createClient(CommunicationService, transport);

export function WelcomeScreen() {
	const { t } = useTranslation("onboarding");
	const navigate = useNavigate();
	const { start, dismiss, reset } = useOnboarding();
	const [loading, setLoading] = useState<"stepper" | "chat" | false>(false);

	const handleStart = useCallback(async () => {
		setLoading("stepper");
		try {
			start("stepper");
			navigate({ to: "/catalog/products", search: { owner_party_id: undefined } });
		} finally {
			setLoading(false);
		}
	}, [start, navigate]);

	const handleStartChat = useCallback(async () => {
		setLoading("chat");
		try {
			const conv = await commClient.createConversation({
				subject: t("onboarding_subject"),
				kind: ConversationKind.AGENT_THREAD,
			});
			start("chat", conv.id);
			navigate({
				to: "/assistant/$threadId",
				params: { threadId: conv.id },
			});
		} catch (e) {
			console.error("Failed to start chat onboarding", e);
			toast.error(t("chat_start_failed"));
			reset();
		} finally {
			setLoading(false);
		}
	}, [start, reset, navigate]);

	return (
		<div className="flex min-h-[calc(100vh-theme(spacing.24))] flex-col items-center justify-center px-4">
			<div className="w-full max-w-md space-y-8">
				{/* Logo / Header */}
				<div className="text-center space-y-3">
					<div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 shadow-sm">
						<Globe className="h-8 w-8 text-primary" />
					</div>
					<div className="space-y-1">
						<h1 className="text-2xl font-semibold tracking-tight">
							{t("welcome_title")}
						</h1>
						<p className="text-sm text-muted-foreground">
							{t("welcome_subtitle")}
						</p>
					</div>
				</div>

				{/* Value props */}
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
					{VALUE_PROPS.map((vp) => (
						<div
							key={vp.titleKey}
							className="flex items-start gap-2.5 rounded-xl border bg-card p-3"
						>
							<div
								className={cn(
									"flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
									vp.color,
								)}
							>
								<vp.icon className="h-4 w-4" />
							</div>
							<div className="min-w-0">
								<p className="text-sm font-medium">{t(vp.titleKey)}</p>
								<p className="text-xs text-muted-foreground leading-relaxed">
									{t(vp.descKey)}
								</p>
							</div>
						</div>
					))}
				</div>

				{/* Two paths */}
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
					<Button
						variant="outline"
						className="group flex flex-col items-start gap-3 rounded-xl bg-card p-4 h-auto justify-start hover:border-primary/40 hover:shadow-sm disabled:opacity-50"
						onClick={handleStart}
						disabled={!!loading}
					>
						<div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 transition-colors group-hover:bg-primary/15">
							<ArrowRight className="h-4 w-4 text-primary" />
						</div>
						<div>
							<p className="text-sm font-medium">{t("path_stepper")}</p>
							<p className="text-xs text-muted-foreground leading-relaxed">
								{t("path_stepper_desc")}
							</p>
						</div>
						{loading === "stepper" && (
							<Loader2 className="h-4 w-4 animate-spin text-primary" />
						)}
					</Button>
					<Button
						variant="outline"
						className="group flex flex-col items-start gap-3 rounded-xl bg-card p-4 h-auto justify-start hover:border-primary/40 hover:shadow-sm disabled:opacity-50"
						onClick={handleStartChat}
						disabled={!!loading}
					>
						<div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 transition-colors group-hover:bg-primary/15">
							<MessageCircle className="h-4 w-4 text-primary" />
						</div>
						<div>
							<p className="text-sm font-medium">{t("path_chat")}</p>
							<p className="text-xs text-muted-foreground leading-relaxed">
								{t("path_chat_desc")}
							</p>
						</div>
						{loading === "chat" && (
							<Loader2 className="h-4 w-4 animate-spin text-primary" />
						)}
					</Button>
				</div>

				{/* Info + Skip */}
				<p className="text-center text-xs text-muted-foreground leading-relaxed">
					{t("skip_info")}{" "}
					<Button
						variant="link"
						size="sm"
						className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
						onClick={() => {
							dismiss();
							navigate({ to: "/catalog/products", search: { owner_party_id: undefined } });
						}}
					>
						{t("skip")}
					</Button>
				</p>
			</div>
		</div>
	);
}
